/**
 * Search in the top bar: the tools by name, tags, summary and world; the pages
 * (Glossary, Sources & method); and what lies behind the tools — glossary
 * terms, names, cards, hexagrams, exhibits, mansions, stars — from the content
 * index (data/search-index.js, loaded on first use). Accents and case are
 * ignored: "ayanamsa" finds Ayanāṃśa, "mahasiah" Mahaſiah. A hyphen is a
 * space or nothing, and the query's spaces may be left out: "one time pad"
 * finds One-Time Pad, "rot 13" rot13, "sha 256" SHA-256. Several words find
 * what holds every one of them, each at the start of a word — a tool in any
 * of its fields: "tarot spread", "hebrew calendar" — ranked after what holds
 * them as one phrase. A few other spellings are read as the word the tools
 * use (ALIASES). A word no tool uses finds nothing: the tools' words are their
 * tags in the registry. Pure: navigation.js and the tests.
 */

import { TOOLS, PAGES, groupById, toolById, pageById } from '../registry.js';

/** Text for matching: compatibility forms (ſ → s), no accents, lower case. */
export const fold = (text) =>
  String(text)
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase();

/** A query as matched: folded, trimmed, one space between its words. */
const normalise = (query) => fold(query).trim().replace(/\s+/g, ' ');

/** The words of a folded text. */
const wordsOf = (text) => text.split(/[\s&/,()\p{Pd}]+/u);

// A hyphen or dash inside a word (One-Time, Diffie–Hellman, SHA-256) read as a space, or left out
const spaced = (text) => text.replace(/(\S)\p{Pd}+(?=\S)/gu, '$1 ');
const unhyphenated = (text) => text.replace(/(\S)\p{Pd}+(?=\S)/gu, '$1');
/** A query with its spaces left out as well: "rot 13" is rot13, "base 64" base64. */
const joined = (q) => q.replace(/(\S)[\s\p{Pd}]+(?=\S)/gu, '$1');

/**
 * Other spellings of a word the tools use, read as that word: one way, and
 * ranked just below the query as typed. Only established equivalents — the
 * usual transliterations of Kabbalah, the astrologers' names for the
 * ascendant, the adjective of Maya — never a tool's name.
 */
const ALIASES = [
  [['kabala', 'kabbala', 'cabala', 'cabbala', 'qabala', 'qabalah'], 'kabbalah'],
  [['rising sign', 'rising'], 'ascendant'],
  [['mayan'], 'maya'],
];

// A match through an alias, or with a hyphen or space read otherwise, ranks just below the same match as typed
const PENALTY = 0.5;
// Every word found apart ranks below the whole query found as one phrase (0–7, plus penalties)
const EVERY_WORD = 10;

/** A query's readings: as typed, then with each alias in it read as its word. */
function readingsOf(q) {
  const words = q.split(' ');
  const readings = [{ q, penalty: 0 }];
  for (const [aliases, word] of ALIASES) {
    for (const alias of aliases) {
      const a = alias.split(' ');
      const at = words.findIndex((_, i) => a.every((w, j) => words[i + j] === w));
      if (at >= 0) readings.push({ q: [...words.slice(0, at), word, ...words.slice(at + a.length)].join(' '), penalty: PENALTY });
    }
  }
  return readings;
}

/** Of several scores, the best (lowest) that matches; -1 when none does. */
const bestOf = (scores) => scores.reduce((top, s) => (s >= 0 && (top < 0 || s < top) ? s : top), -1);

/** The best score of an item over a query's readings; -1 when no reading matches. */
const overReadings = (readings, score) =>
  bestOf(
    readings.map(({ q, penalty }) => {
      const s = score(q);
      return s < 0 ? -1 : s + penalty;
    }),
  );

/**
 * A text matched three ways — as typed; with hyphens as spaces on both sides;
 * with the query's spaces and hyphens and the texts' hyphens left out — the
 * last two a PENALTY below. A text's own spaces always stay, so "words" never
 * meets Word Splitter; and the third counts only below `joinedBelow`, at a
 * name's or a tag's start, not inside one, where "eras" would meet
 * inter-aspects.
 */
function inThreeForms(texts, q, score, joinedBelow) {
  const forms = [
    [texts.asTyped, q, 0, Infinity],
    [texts.spaced, spaced(q), PENALTY, Infinity],
    [texts.unhyphenated, joined(q), PENALTY, joinedBelow],
  ];
  return bestOf(
    forms.map(([form, query, penalty, below]) => {
      const s = score(form, query);
      return s < 0 || s >= below ? -1 : s + penalty;
    }),
  );
}

/** A record of texts in the three forms: `make` is given the function that turns a text into each form. */
const threeForms = (make) => ({ asTyped: make((t) => t), spaced: make(spaced), unhyphenated: make(unhyphenated) });

/** How a folded name matches: 0 it is the query, 1 it starts with it, 2 a word in it does, 3 it contains it (from three letters); -1 not at all. */
function nameScore(n, q) {
  if (n === q) return 0;
  if (n.startsWith(q)) return 1;
  if (wordsOf(n).some((w) => w.startsWith(q))) return 2;
  return q.length >= 3 && n.includes(q) ? 3 : -1;
}

/** Each item's texts folded once, not on every keystroke. */
const foldedTexts = new WeakMap();
function textsOf(item, make) {
  let texts = foldedTexts.get(item);
  if (!texts) foldedTexts.set(item, (texts = make(item)));
  return texts;
}

/** A one-letter word or a number is matched only as a whole word: "i c" is not every tool with an i and a c, "13" not m-134-c. */
const isWholeOnly = (w) => w.length === 1 || /^\d+$/.test(w);

/** One word of several at the start of a word among `words`, or equal to one when it must be whole. */
const startsAWord = (words, w) => (isWholeOnly(w) ? words.includes(w) : words.some((word) => word.startsWith(w)));

/** Words of a query, each matched on its own: EVERY_WORD + the weakest word's score; -1 when a word is missing. */
function everyWordScore(words, wordScore) {
  let worst = 0;
  for (const w of words) {
    const s = wordScore(w);
    if (s < 0) return -1;
    worst = Math.max(worst, s);
  }
  return EVERY_WORD + worst;
}

// ─── Tools ───────────────────────────────────────────────────────────────────

const toolTexts = (tool) =>
  textsOf(tool, () => {
    const label = fold(tool.label);
    const tags = (tool.tags ?? []).map(fold);
    const summary = fold(tool.summary);
    const world = fold(groupById[tool.group].label);
    return {
      ...threeForms((as) => ({ label: as(label), tags: tags.map(as), summary: as(summary), world: as(world) })),
      labelWords: wordsOf(label),
      tagWords: tags.flatMap(wordsOf),
      summaryWords: wordsOf(summary),
      worldWords: wordsOf(world),
    };
  });

/** How a tool's fields hold a text: in its name (0–2), at a tag's start (3), inside its name (4) or a tag (5), in its summary (6) or its world's name (7); -1 not at all. */
function fieldsScore(f, q) {
  const label = nameScore(f.label, q);
  if (label >= 0 && label < 3) return label;
  if (f.tags.some((tag) => tag.startsWith(q))) return 3;
  if (label === 3) return 4;
  if (f.tags.some((tag) => tag.includes(q))) return 5;
  if (f.summary.includes(q)) return 6;
  if (f.world.includes(q)) return 7;
  return -1;
}

// Left out together, the query's spaces and the fields' hyphens count only in a name or at a tag's start (0–3)
const toolTextScore = (t, q) => inThreeForms(t, q, fieldsScore, 4);

/**
 * One word of several: in the tool's name or at a tag's start (0–3), else at
 * the start of a word in a tag (5), its summary (6) or its world's name (7) —
 * never inside a word, which several short words would meet everywhere.
 */
function toolWordScore(t, w) {
  if (isWholeOnly(w)) {
    if (t.labelWords.includes(w)) return 2;
  } else {
    const phrase = toolTextScore(t, w);
    if (phrase >= 0 && phrase < 4) return phrase;
  }
  if (startsAWord(t.tagWords, w)) return 5;
  if (startsAWord(t.summaryWords, w)) return 6;
  return startsAWord(t.worldWords, w) ? 7 : -1;
}

/** The whole query as one phrase (0–8), else every word apart (EVERY_WORD + 0–7). */
function toolScore(tool, q) {
  const t = toolTexts(tool);
  const phrase = toolTextScore(t, q);
  if (phrase >= 0 || !q.includes(' ')) return phrase;
  return everyWordScore(q.split(' '), (w) => toolWordScore(t, w));
}

const best = (items, score, limit) =>
  items
    .map((item, order) => ({ item, order, score: score(item) }))
    .filter((r) => r.score >= 0)
    .sort((a, b) => a.score - b.score || a.order - b.order)
    .slice(0, limit)
    .map((r) => r.item);

export function searchTools(query, limit = 8) {
  const q = normalise(query);
  if (!q) return [];
  const readings = readingsOf(q);
  return best(TOOLS, (tool) => overReadings(readings, (r) => toolScore(tool, r)), limit);
}

// ─── Pages ───────────────────────────────────────────────────────────────────

const pageTexts = (page) =>
  textsOf(page, () => {
    const label = fold(page.label);
    return { ...threeForms((as) => as(label)), words: wordsOf(label) };
  });

/** A page by its name: the phrase (0–3), else every word at the start of a word in it (EVERY_WORD + 0–2). */
function pageScore(page, q) {
  const p = pageTexts(page);
  const phrase = inThreeForms(p, q, nameScore, 3);
  if (phrase >= 0 || !q.includes(' ')) return phrase;
  return everyWordScore(q.split(' '), (w) => {
    if (isWholeOnly(w)) return p.words.includes(w) ? 2 : -1;
    const s = inThreeForms(p, w, nameScore, 3);
    return s < 3 ? s : -1;
  });
}

export function searchPages(query, limit = 2) {
  const q = normalise(query);
  if (q.length < 2) return [];
  const readings = readingsOf(q);
  return best(PAGES, (page) => overReadings(readings, (r) => pageScore(page, r)), limit);
}

// ─── What lies behind the tools ──────────────────────────────────────────────

const entryTexts = (entry) =>
  textsOf(entry, ([, , label, , also]) => {
    const names = [label, ...also].map(fold);
    return { ...threeForms((as) => names.map(as)), words: names.map(wordsOf) };
  });

/** A name's place among an entry's names: the label (0–2) before the other names (3–5), and a name's start before a match inside one (6, 7). */
const rankOfName = (s, i) => (s < 0 ? -1 : s === 3 ? 6 + Math.min(i, 1) : s + (i ? 3 : 0));

/** How an entry's names hold a text (0–7). */
const namesScore = (names, q) => bestOf(names.map((n, i) => rankOfName(nameScore(n, q), i)));

// Left out together, the query's spaces and the names' hyphens count only at a name's or a word's start (0–5)
const entryTextScore = (e, q) => inThreeForms(e, q, namesScore, 6);

/** The phrase (0–8), else every word at the start of a name or a word in one, never inside a word (EVERY_WORD + 0–5). */
function entryScore(entry, q) {
  const e = entryTexts(entry);
  const phrase = entryTextScore(e, q);
  if (phrase >= 0 || !q.includes(' ')) return phrase;
  return everyWordScore(q.split(' '), (w) => {
    if (isWholeOnly(w)) return bestOf(e.words.map((words, i) => (words.includes(w) ? rankOfName(2, i) : -1)));
    const s = entryTextScore(e, w);
    return s < 6 ? s : -1;
  });
}

/** Content entries ([kind, key, label, note, also]) by their label, then the other names they go by. */
export function searchContent(index, query, limit = 6) {
  const q = normalise(query);
  if (q.length < 2) return [];
  const readings = readingsOf(q);
  return best(index, (entry) => overReadings(readings, (r) => entryScore(entry, r)), limit);
}

/** A tarot card's group in the tool's pills, from its id (major-00, minor-wands-09). */
const cardGroup = (id) => (id.startsWith('major-') ? 'trumps' : id.split('-')[1]);

/**
 * Where each kind of content opens: a tool and the state (the share-link map
 * of field and mode ids) that shows it there, or a page and its heading. The
 * ids are checked against each tool's own markup in test-tools.mjs.
 */
export const CONTENT_KINDS = {
  term: { page: 'glossary', open: (key) => `#term-${key}` },
  name: { tool: 'shemhamephorash', open: (key) => ({ 'shm-name': key }) },
  enochian: {
    tool: 'enochian',
    open: (key) => {
      const [year, table, name] = key.split('/');
      return { 'eno-tabs': year, 'eno-table': table, 'eno-name': name };
    },
  },
  exhibit: { tool: 'archive', open: (key) => ({ 'arc-list': key }) },
  card: { tool: 'tarot', open: (key) => ({ 'tar-tabs': 'deck', 'tar-group': cardGroup(key), 'tar-card': key }) },
  hexagram: { tool: 'iching', open: (key) => ({ 'iching-tabs': 'book', 'iching-hex': key }) },
  mansion: { tool: 'mansions', open: (key) => ({ 'lmn-mansion': key }) },
  star: { tool: 'fixedstars', open: (key) => ({ 'fxs-tabs': 'stars', 'fxs-star': key }) },
};

/** Where a content entry opens: { tool, state } or { page, hash }. */
export function contentTarget([kind, key]) {
  const k = CONTENT_KINDS[kind];
  return k.page ? { page: k.page, hash: k.open(key) } : { tool: k.tool, state: k.open(key) };
}

/** The line under a content result: where it is (the tool's or page's name), and what it is there. */
export function contentMeta([kind, , , note]) {
  const k = CONTENT_KINDS[kind];
  const where = k.page ? pageById[k.page].label : toolById[k.tool].label;
  return note ? `${where} / ${note}` : where;
}
